/**
 * Shared types + helpers for admin-run urgency campaigns.
 *
 * IMPORTANT: nothing here reads real inventory. Campaigns and per-product
 * labels are entirely admin-authored, so the operator owns every claim
 * shown on the storefront.
 */

export const URGENCY_KINDS = [
  { value: "festive", label: "Festive sale" },
  { value: "payday", label: "Payday sale" },
  { value: "flash", label: "Limited-time price / flash sale" },
  { value: "weekend", label: "Weekend sale" },
  { value: "custom", label: "Custom" },
] as const;

export type UrgencyKind = (typeof URGENCY_KINDS)[number]["value"];

export const URGENCY_PLACEMENTS = [
  { value: "strip", label: "Top strip (site-wide)" },
  { value: "pdp", label: "Product page price block" },
  { value: "cart", label: "Cart drawer banner" },
  { value: "home", label: "Homepage ribbon" },
] as const;

export type UrgencyPlacement = (typeof URGENCY_PLACEMENTS)[number]["value"];

export const URGENCY_ACCENTS = [
  { value: "brand", label: "Brand blue" },
  { value: "charcoal", label: "Charcoal" },
  { value: "festive", label: "Festive gold" },
] as const;

export type UrgencyAccent = (typeof URGENCY_ACCENTS)[number]["value"];

export type UrgencyCampaign = {
  id: string;
  name: string;
  kind: UrgencyKind;
  headline: string;
  subline: string | null;
  coupon_code: string | null;
  accent: UrgencyAccent;
  starts_at: string | null;
  ends_at: string | null;
  placements: UrgencyPlacement[];
  is_enabled: boolean;
};

export type ProductUrgencyLabel = {
  id: string;
  product_handle: string;
  label: string;
  is_enabled: boolean;
};

export type CampaignStatus = "live" | "scheduled" | "ended" | "off";

export function campaignStatus(
  c: Pick<UrgencyCampaign, "is_enabled" | "starts_at" | "ends_at">,
  now: number = Date.now(),
): CampaignStatus {
  if (!c.is_enabled) return "off";
  if (c.ends_at && new Date(c.ends_at).getTime() <= now) return "ended";
  if (c.starts_at && new Date(c.starts_at).getTime() > now) return "scheduled";
  return "live";
}

export function hasPlacement(c: UrgencyCampaign, p: UrgencyPlacement): boolean {
  return Array.isArray(c.placements) && c.placements.includes(p);
}

/** Accent -> Tailwind classes, all semantic brand tokens (no raw hex). */
export function accentClasses(accent: UrgencyAccent): string {
  switch (accent) {
    case "charcoal":
      return "bg-foreground text-background";
    case "festive":
      return "bg-amber-500 text-black";
    case "brand":
    default:
      return "bg-brand text-white";
  }
}

export type Remaining = {
  total: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  expired: boolean;
};

export function remainingUntil(endsAt: string | null, now: number = Date.now()): Remaining | null {
  if (!endsAt) return null;
  const total = new Date(endsAt).getTime() - now;
  if (!Number.isFinite(total)) return null;
  const clamped = Math.max(0, total);
  const seconds = Math.floor(clamped / 1000) % 60;
  const minutes = Math.floor(clamped / 60000) % 60;
  const hours = Math.floor(clamped / 3_600_000) % 24;
  const days = Math.floor(clamped / 86_400_000);
  return { total: clamped, days, hours, minutes, seconds, expired: total <= 0 };
}

export function formatRemaining(r: Remaining): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  if (r.days > 0) return `${r.days}d ${pad(r.hours)}:${pad(r.minutes)}:${pad(r.seconds)}`;
  return `${pad(r.hours)}:${pad(r.minutes)}:${pad(r.seconds)}`;
}
